import { useEffect, useMemo, useRef, useState } from "react";
import { Database, Upload, Download, DownloadCloud } from "lucide-react";
import CustomInputSwitch from "@/components/desktop/form/input/input-switch/CustomInputSwitch";
import CustomButton from "@/components/desktop/button/CustomButton";
import styles from "./integration-form.module.scss";

const getRowId = (db, idx) => db?.business_id ?? db?.code ?? idx;
const toBool = (v) => v === true || v === 1 || v === "1";

const normalize = (db, idx) => ({
	...db,
	active_purchase: toBool(db?.active_purchase) ? 1 : 0,
	active_consume: toBool(db?.active_consume) ? 1 : 0,
	__rid: getRowId(db, idx),
});

const idsSignature = (list = []) => list.map((db, i) => String(getRowId(db, i))).join("|");

const IntegrationDatabases = ({ integration, onToggle, isProduction, onImportExcel, onExportExcel }) => {
	const initial = useMemo(() => (integration?.databases || []).map(normalize), [integration]);
	const [rows, setRows] = useState(initial);
	const [sig, setSig] = useState(() => idsSignature(integration?.databases || []));
	const fileRefs = useRef({});
	const globalFileRef = useRef(null);

	useEffect(() => {
		const incoming = integration?.databases || [];
		const nextSig = idsSignature(incoming);
		if (nextSig !== sig) {
			setRows(incoming.map(normalize));
			setSig(nextSig);
		}
	}, [integration, sig]);

	const handleToggle = (rowId, field) => {
		setRows((prev) => prev.map((db) => (db.__rid === rowId ? { ...db, [field]: db[field] ? 0 : 1 } : db)));
		if (typeof onToggle === "function") onToggle(rowId, field);
	};

	const handleExport = (codeOrId) => {
		if (typeof onExportExcel === "function") onExportExcel(codeOrId);
	};

	const handleExportGlobal = () => {
		if (typeof onExportExcel === "function") onExportExcel(null);
	};

	const handleImportClick = (rowId) => {
		const input = fileRefs.current[rowId];
		if (input) input.click();
	};

	const handleFileChange = (rowId, e) => {
		const file = e.target.files && e.target.files[0];
		if (file && typeof onImportExcel === "function") onImportExcel(rowId, file);
		e.target.value = "";
	};

	const handleImportGlobalClick = () => {
		if (globalFileRef.current) globalFileRef.current.click();
	};

	const handleGlobalFileChange = (e) => {
		const file = e.target.files && e.target.files[0];
		if (file && typeof onImportExcel === "function") onImportExcel(null, file);
		e.target.value = "";
	};

	const list = useMemo(() => {
		const wantIsTest = isProduction ? 0 : 1;
		return (rows || []).filter((db) => Number(db.is_test) === wantIsTest);
	}, [rows, isProduction]);

	const customList = list.filter((db) => db.custom_filter && String(db.custom_filter).trim().length > 0);

	return (
		<section className={styles.dbSection} aria-label="Bases de datos integradas">
			<div className={styles.envHeader}>
				<div className={styles.envTitles}>
					<h2 className={styles.envTitle}>Bases de Datos</h2>
					<p className={styles.envSubtitle}>
						Gestiona por base: habilita Compras y Consumos y sincroniza códigos vía Excel (descargar o cargar masivamente).
					</p>
				</div>

				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<CustomButton
						variant="secondary"
						size="sm"
						onClick={handleExportGlobal}
						startIcon={<DownloadCloud size={16} />}
						ariaLabel="Descargar Excel global (todas las bases)"
					>
						Descargar global
					</CustomButton>

					<input ref={globalFileRef} type="file" accept=".xlsx,.xls,.csv" className={styles.fileInputHidden} onChange={handleGlobalFileChange} />

					<CustomButton
						variant="secondary"
						size="sm"
						onClick={handleImportGlobalClick}
						startIcon={<Upload size={16} />}
						ariaLabel="Cargar Excel global (todas las bases)"
					>
						Cargar global
					</CustomButton>
				</div>
			</div>

			{customList.length > 0 && (
				<ul className={styles.dbList} aria-live="polite">
					{list.map((db, idx) => {
						const rowId = db.__rid ?? getRowId(db, idx);
						const activePurchase = !!db.active_purchase;
						const activeConsume = !!db.active_consume;

						return (
							<li
								key={rowId}
								className={styles.dbItem}
								aria-label={`Base ${db.custom_filter}, compras ${activePurchase ? "activadas" : "desactivadas"}, consumos ${
									activeConsume ? "activados" : "desactivados"
								}`}
							>
								<div className={styles.rowLeft}>
									<Database className={styles.dbIcon} aria-hidden="true" />
									<div className={styles.dbLabels}>
										<div className={styles.dbFilter} title={db.custom_filter}>
											{db.custom_filter || "GLOBAL"}
										</div>
									</div>
								</div>

								<div className={styles.rowCenter}>
									<div className={styles.toggleInline}>
										<span className={styles.toggleText}>Compras</span>
										<div className={styles.switchShort}>
											<CustomInputSwitch
												value={activePurchase}
												onChange={() => handleToggle(rowId, "active_purchase")}
												optionLeft="No"
												optionRight="Sí"
											/>
										</div>
									</div>

									<div className={styles.toggleInline}>
										<span className={styles.toggleText}>Consumos</span>
										<div className={styles.switchShort}>
											<CustomInputSwitch
												value={activeConsume}
												onChange={() => handleToggle(rowId, "active_consume")}
												optionLeft="No"
												optionRight="Sí"
											/>
										</div>
									</div>
								</div>

								<div className={styles.rowRight}>
									<CustomButton
										variant="secondary"
										size="sm"
										onClick={() => handleExport(db.code)}
										startIcon={<Download size={16} />}
										ariaLabel={`Descargar Excel de ${db.custom_filter}`}
									>
										Descargar
									</CustomButton>

									<input
										ref={(el) => (fileRefs.current[rowId] = el)}
										type="file"
										accept=".xlsx,.xls,.csv"
										className={styles.fileInputHidden}
										onChange={(e) => handleFileChange(db.custom_filter, e)}
									/>

									<CustomButton
										variant="secondary"
										size="sm"
										onClick={() => handleImportClick(rowId)}
										startIcon={<Upload size={16} />}
										ariaLabel={`Cargar Excel de ${db.custom_filter}`}
									>
										Cargar
									</CustomButton>
								</div>
							</li>
						);
					})}
				</ul>
			)}
		</section>
	);
};

export default IntegrationDatabases;
